import React, { useState, useEffect, useRef } from 'react';
import CodeEditor from '@uiw/react-textarea-code-editor';
import './SignatureEditor.css';

interface SignatureEditorProps {
  signature: { id: string; name: string; html: string } | null;
  onSave: (name: string, html: string) => void;
}

const SignatureEditor: React.FC<SignatureEditorProps> = ({ signature, onSave }) => {
  const [name, setName] = useState('');
  const [html, setHtml] = useState('');
  const [viewMode, setViewMode] = useState<'wysiwyg' | 'code' | 'preview' | 'split'>('wysiwyg');
  const [showPlaceholderMenu, setShowPlaceholderMenu] = useState(false);
  const editorRef = React.useRef<HTMLTextAreaElement>(null);
  const wysiwygRef = useRef<HTMLDivElement>(null);
  const isTypingRef = useRef(false);

  useEffect(() => {
    if (signature) {
      setName(signature.name);
      setHtml(signature.html);
      isTypingRef.current = false; // Mark as loading, not typing
    } else {
      setName('');
      const template = getDefaultTemplate();
      setHtml(template);
      isTypingRef.current = false; // Mark as loading, not typing
    }
  }, [signature]);

  // Sync to WYSIWYG editor when html changes and we're NOT actively typing
  useEffect(() => {
    if (wysiwygRef.current && viewMode === 'wysiwyg' && html && !isTypingRef.current) {
      wysiwygRef.current.innerHTML = html;
    }
  }, [html, viewMode]);

  const getDefaultTemplate = () => {
    return `<div style="font-family: Arial, sans-serif; font-size: 14px; color: #333;">
  <table cellpadding="0" cellspacing="0" style="border-collapse: collapse;">
    <tr>
      <td style="padding-right: 20px; border-right: 2px solid #1A7A85;">
        <img src="https://stampraddin.blob.core.windows.net/images/stamp_logo.png" alt="Logo" style="width: 80px; height: 80px;">
      </td>
      <td style="padding-left: 20px;">
        <div>
          <strong style="font-size: 18px; color: #1A7A85;">{{displayName}}</strong><br>
          <span style="color: #666;">{{jobTitle}}</span><br>
          <span style="color: #666;">{{companyName}}</span>
        </div>
        <div style="margin-top: 10px;">
          <a href="mailto:{{email}}" style="color: #1A7A85; text-decoration: none;">
            <img src="https://stampraddin.blob.core.windows.net/images/stamp_logo.png" alt="Email" style="width: 14px; height: 14px; vertical-align: middle; margin-right: 4px;" />
            {{email}}
          </a><br>
          <span style="color: #666;">📱 {{phoneNumber}}</span><br>
          <a href="https://www.company.com" style="color: #1A7A85; text-decoration: none;">🌐 www.company.com</a>
        </div>
      </td>
    </tr>
  </table>
</div>`;
  };

  const handleSave = () => {
    onSave(name || 'Untitled Signature', html);
  };

  const handleCopyHtml = () => {
    navigator.clipboard.writeText(html);
    alert('HTML copied to clipboard!');
  };

  const insertTemplate = (template: string) => {
    setHtml(template);
    // Also update WYSIWYG editor if in that mode
    if (wysiwygRef.current && viewMode === 'wysiwyg') {
      wysiwygRef.current.innerHTML = template;
    }
  };

  const placeholders = [
    { label: 'Display Name', value: '{{displayName}}', description: "User's full name" },
    { label: 'Email', value: '{{email}}', description: "User's email address" },
    { label: 'Job Title', value: '{{jobTitle}}', description: "User's job title" },
    { label: 'Department', value: '{{department}}', description: "User's department" },
    { label: 'Phone Number', value: '{{phoneNumber}}', description: "User's business phone" },
    { label: 'Mobile Phone', value: '{{mobilePhone}}', description: "User's mobile phone" },
    { label: 'Office Location', value: '{{officeLocation}}', description: "User's office location" },
    { label: 'Company Name', value: '{{companyName}}', description: "Organization name" },
  ];

  const getPreviewHtml = () => {
    // Replace placeholders with sample data for preview
    return html
      .replace(/\{\{displayName\}\}/g, 'John Smith')
      .replace(/\{\{email\}\}/g, 'john.smith@company.com')
      .replace(/\{\{jobTitle\}\}/g, 'Senior Manager')
      .replace(/\{\{department\}\}/g, 'Sales & Marketing')
      .replace(/\{\{phoneNumber\}\}/g, '+1 (555) 123-4567')
      .replace(/\{\{mobilePhone\}\}/g, '+1 (555) 987-6543')
      .replace(/\{\{officeLocation\}\}/g, 'Boise, ID')
      .replace(/\{\{companyName\}\}/g, 'Stampr Inc.');
  };

  const insertPlaceholder = (placeholder: string) => {
    const textarea = editorRef.current;
    if (!textarea) {
      // If we can't access the textarea, just append to the end
      setHtml(html + placeholder);
      setShowPlaceholderMenu(false);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newHtml = html.substring(0, start) + placeholder + html.substring(end);
    setHtml(newHtml);

    // Set cursor position after the inserted placeholder
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + placeholder.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);

    setShowPlaceholderMenu(false);
  };

  // WYSIWYG Editor Functions
  const execCommand = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value);
    wysiwygRef.current?.focus();
  };

  const handleWysiwygInput = () => {
    if (wysiwygRef.current) {
      isTypingRef.current = true; // Mark that user is typing
      setHtml(wysiwygRef.current.innerHTML);
    }
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const changeFontSize = (e: React.ChangeEvent<HTMLSelectElement>) => {
    execCommand('fontSize', e.target.value);
  };

  const changeFontFamily = (e: React.ChangeEvent<HTMLSelectElement>) => {
    execCommand('fontName', e.target.value);
  };

  const changeTextColor = (e: React.ChangeEvent<HTMLInputElement>) => {
    execCommand('foreColor', e.target.value);
  };

  const insertWysiwygPlaceholder = (placeholder: string) => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const span = document.createElement('span');
      span.textContent = placeholder;
      // Style as animated gradient badge
      span.className = 'placeholder-badge';
      span.contentEditable = 'false';
      range.deleteContents();
      range.insertNode(span);
      
      // Move cursor after the placeholder
      range.setStartAfter(span);
      range.setEndAfter(span);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    if (wysiwygRef.current) {
      setHtml(wysiwygRef.current.innerHTML);
    }
    setShowPlaceholderMenu(false);
  };

  return (
    <div className="signature-editor">
      <div className="editor-header">
        <div className="editor-title-section">
          <input
            type="text"
            className="signature-name-input"
            placeholder="Signature Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="editor-actions">
          <div className="view-mode-toggle">
            <button
              className={`view-btn ${viewMode === 'wysiwyg' ? 'active' : ''}`}
              onClick={() => setViewMode('wysiwyg')}
              title="Visual Editor"
            >
              📝
            </button>
            <button
              className={`view-btn ${viewMode === 'code' ? 'active' : ''}`}
              onClick={() => setViewMode('code')}
              title="HTML Code"
            >
              &lt;/&gt;
            </button>
            <button
              className={`view-btn ${viewMode === 'split' ? 'active' : ''}`}
              onClick={() => setViewMode('split')}
              title="Split View"
            >
              ⫿
            </button>
            <button
              className={`view-btn ${viewMode === 'preview' ? 'active' : ''}`}
              onClick={() => setViewMode('preview')}
              title="Preview"
            >
              👁
            </button>
          </div>
          <button className="btn-copy" onClick={handleCopyHtml}>
            📋 Copy HTML
          </button>
          <button className="btn-save" onClick={handleSave}>
            💾 Save
          </button>
        </div>
      </div>

      {viewMode !== 'preview' && viewMode === 'wysiwyg' && (
        <div className="template-bar">
          <span className="template-label">Quick Templates:</span>
          <button 
            className="template-btn" 
            onClick={() => insertTemplate(getDefaultTemplate())}
          >
            Professional
          </button>
          <button 
            className="template-btn"
            onClick={() => insertTemplate(getMinimalTemplate())}
          >
            Minimal
          </button>
          <button 
            className="template-btn"
            onClick={() => insertTemplate(getModernTemplate())}
          >
            Modern
          </button>
        </div>
      )}

      {viewMode === 'wysiwyg' && (
        <div className="wysiwyg-toolbar">
          <select onChange={changeFontFamily} className="toolbar-select" defaultValue="Arial">
            <option value="Arial">Arial</option>
            <option value="Helvetica">Helvetica</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Georgia">Georgia</option>
            <option value="Verdana">Verdana</option>
            <option value="Courier New">Courier New</option>
          </select>
          
          <select onChange={changeFontSize} className="toolbar-select" defaultValue="3">
            <option value="1">Small</option>
            <option value="2">Normal</option>
            <option value="3">Medium</option>
            <option value="4">Large</option>
            <option value="5">X-Large</option>
            <option value="6">XX-Large</option>
          </select>

          <div className="toolbar-divider"></div>

          <button onClick={() => execCommand('bold')} className="toolbar-btn" title="Bold (Ctrl+B)">
            <strong>B</strong>
          </button>
          <button onClick={() => execCommand('italic')} className="toolbar-btn" title="Italic (Ctrl+I)">
            <em>I</em>
          </button>
          <button onClick={() => execCommand('underline')} className="toolbar-btn" title="Underline (Ctrl+U)">
            <u>U</u>
          </button>
          <button onClick={() => execCommand('strikeThrough')} className="toolbar-btn" title="Strikethrough">
            <s>S</s>
          </button>

          <div className="toolbar-divider"></div>

          <button onClick={() => execCommand('justifyLeft')} className="toolbar-btn" title="Align Left">
            ≡
          </button>
          <button onClick={() => execCommand('justifyCenter')} className="toolbar-btn" title="Align Center">
            ≣
          </button>
          <button onClick={() => execCommand('justifyRight')} className="toolbar-btn" title="Align Right">
            ≢
          </button>

          <div className="toolbar-divider"></div>

          <button onClick={() => execCommand('insertUnorderedList')} className="toolbar-btn" title="Bullet List">
            • List
          </button>
          <button onClick={() => execCommand('insertOrderedList')} className="toolbar-btn" title="Numbered List">
            1. List
          </button>

          <div className="toolbar-divider"></div>

          <button onClick={insertLink} className="toolbar-btn" title="Insert Link">
            🔗
          </button>
          <label className="toolbar-btn color-picker-label" title="Text Color">
            A
            <input
              type="color"
              onChange={changeTextColor}
              className="color-picker-input"
              defaultValue="#000000"
            />
          </label>

          <div className="toolbar-divider"></div>

          <div className="placeholder-menu-container">
            <button 
              className="template-btn placeholder-btn"
              onClick={() => setShowPlaceholderMenu(!showPlaceholderMenu)}
            >
              🏷️ Insert Placeholder
            </button>
            {showPlaceholderMenu && (
              <div className="placeholder-dropdown">
                <div className="placeholder-dropdown-header">
                  <span>Dynamic Placeholders</span>
                  <button 
                    className="close-btn"
                    onClick={() => setShowPlaceholderMenu(false)}
                  >
                    ✕
                  </button>
                </div>
                <div className="placeholder-list">
                  {placeholders.map((placeholder) => (
                    <button
                      key={placeholder.value}
                      className="placeholder-item"
                      onClick={() => viewMode === 'wysiwyg' ? insertWysiwygPlaceholder(placeholder.value) : insertPlaceholder(placeholder.value)}
                    >
                      <span className="placeholder-label">{placeholder.label}</span>
                      <span className="placeholder-value">{placeholder.value}</span>
                      <span className="placeholder-description">{placeholder.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={`editor-container ${viewMode}`}>
        {viewMode === 'wysiwyg' && (
          <div className="wysiwyg-panel">
            <div
              ref={wysiwygRef}
              className="wysiwyg-content"
              contentEditable
              onInput={handleWysiwygInput}
              suppressContentEditableWarning
            />
          </div>
        )}

        {viewMode === 'code' && (
          <div className="code-panel">
            <div className="panel-label">HTML Code</div>
            <CodeEditor
              value={html}
              language="html"
              placeholder="Enter your HTML signature code here..."
              onChange={(e) => setHtml(e.target.value)}
              padding={15}
              ref={editorRef}
              style={{
                fontSize: 13,
                backgroundColor: '#f8f9fa',
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                minHeight: '400px',
                borderRadius: '8px',
              }}
            />
          </div>
        )}

        {viewMode === 'split' && (
          <>
            <div className="code-panel">
              <div className="panel-label">HTML Code</div>
              <CodeEditor
                value={html}
                language="html"
                placeholder="Enter your HTML signature code here..."
                onChange={(e) => setHtml(e.target.value)}
                padding={15}
                ref={editorRef}
                style={{
                  fontSize: 13,
                  backgroundColor: '#f8f9fa',
                  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                  minHeight: '400px',
                  borderRadius: '8px',
                }}
              />
            </div>
            <div className="preview-panel">
              <div className="panel-label">Preview</div>
              <div className="preview-note">
                <strong>Note:</strong> Dynamic placeholders have been filled with generic information to preview styling. Deployed signatures will use the information from the user's M365 profile when deployed.
              </div>
              <div className="preview-content" dangerouslySetInnerHTML={{ __html: getPreviewHtml() }} />
            </div>
          </>
        )}

        {viewMode === 'preview' && (
          <div className="preview-panel">
            <div className="panel-label">Preview</div>
            <div className="preview-note">
              <strong>Note:</strong> Dynamic placeholders have been filled with generic information to preview styling. Deployed signatures will use the information from the user's M365 profile when deployed.
            </div>
            <div className="preview-content" dangerouslySetInnerHTML={{ __html: getPreviewHtml() }} />
          </div>
        )}
      </div>

      <div className="editor-tips">
        <h3>💡 Tips for Email Signatures:</h3>
        <ul>
          <li>Use dynamic placeholders like {'{{displayName}}'}, {'{{email}}'}, {'{{jobTitle}}'} to autofill content from M365 user profile</li>
          <li>Use tables for layout instead of divs</li>
          <li>Keep images under 100KB for faster loading</li>
          <li>Include alt text for images</li>
        </ul>
      </div>
    </div>
  );
};

function getMinimalTemplate() {
  return `<div style="font-family: Arial, sans-serif; font-size: 14px; color: #333;">
  <p style="margin: 0;">
    <strong>{{displayName}}</strong><br>
    {{jobTitle}} | {{companyName}}<br>
    <a href="mailto:{{email}}" style="color: #1A7A85; text-decoration: none;">{{email}}</a> | {{phoneNumber}}
  </p>
</div>`;
}

function getModernTemplate() {
  return `<div style="font-family: 'Segoe UI', Arial, sans-serif; font-size: 14px; color: #333;">
  <table cellpadding="0" cellspacing="0" style="border-collapse: collapse;">
    <tr>
      <td>
        <div style="background: linear-gradient(135deg, #1A7A85 0%, #34424F 100%); padding: 20px; border-radius: 10px; color: white;">
          <h2 style="margin: 0 0 10px 0; font-size: 20px;">{{displayName}}</h2>
          <p style="margin: 0; font-size: 14px; opacity: 0.9;">{{jobTitle}}</p>
          <p style="margin: 0; font-size: 14px; opacity: 0.9;">{{companyName}}</p>
          <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid rgba(255,255,255,0.3);">
            <p style="margin: 5px 0;">
              <img src="https://stampraddin.blob.core.windows.net/images/stamp_logo.png" alt="Email" style="width: 14px; height: 14px; vertical-align: middle; margin-right: 4px;" />
              {{email}}
            </p>
            <p style="margin: 5px 0;">📱 {{phoneNumber}}</p>
            <p style="margin: 5px 0;">🌐 www.yourcompany.com</p>
          </div>
        </div>
      </td>
    </tr>
  </table>
</div>`;
}

export default SignatureEditor;
